import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSocket } from '../../hooks/useSocket';

interface VideoCallProps {
  isActive: boolean;
  isMicMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  roomId?: string;
  activeTab?: string;
  onStartCall: () => void;
  onEndCall: () => void;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onScreenShare: () => void;
}

interface Participant {
  userId: string;
  username: string;
  mediaState: {
    video: boolean;
    audio: boolean;
    screen: boolean;
  };
  stream?: MediaStream;
}

const VideoCall: React.FC<VideoCallProps> = ({
  isActive,
  isMicMuted,
  isCameraOff,
  isScreenSharing,
  roomId,
  activeTab = 'video',
  onStartCall,
  onEndCall,
  onToggleMic,
  onToggleCamera,
  onScreenShare
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const { socket } = useSocket();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [peerConnections, setPeerConnections] = useState<Map<string, RTCPeerConnection>>(new Map());
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed'>('connecting');
  const [screenShareStream, setScreenShareStream] = useState<MediaStream | null>(null);

  // ICE servers configuration
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  // Ensure video element gets the stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.muted = true;

      const playVideo = async () => {
        try {
          await localVideoRef.current?.play();
          console.log('Local video playing successfully');
        } catch (playError) {
          console.warn('Auto-play prevented for local video:', playError);
        }
      };

      playVideo();
    }
  }, [localStream]);

  // Start call duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isActive && callStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const duration = Math.floor((now.getTime() - callStartTime.getTime()) / 1000);
        setCallDuration(duration);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, callStartTime]);

  // Socket event listeners for WebRTC signaling
  useEffect(() => {
    if (!socket || !isActive || !roomId) return;

    const handleParticipantJoined = (data: { participant: Participant }) => {
      setParticipants(prev => [...prev.filter(p => p.userId !== data.participant.userId), data.participant]);

      // Create peer connection for new participant
      createPeerConnection(data.participant.userId);
    };

    const handleParticipantLeft = (data: { userId: string }) => {
      setParticipants(prev => prev.filter(p => p.userId !== data.userId));

      // Close peer connection
      const pc = peerConnections.get(data.userId);
      if (pc) {
        pc.close();
        setPeerConnections(prev => {
          const newMap = new Map(prev);
          newMap.delete(data.userId);
          return newMap;
        });
      }
    };

    const handleParticipantsUpdate = (data: { participants: Participant[] }) => {
      setParticipants(data.participants);
    };

    const handleOffer = async (data: { fromUserId: string; offer: RTCSessionDescriptionInit }) => {
      const pc = peerConnections.get(data.fromUserId) || createPeerConnection(data.fromUserId);

      try {
        await pc.setRemoteDescription(data.offer);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit('video:answer', {
          roomId,
          targetUserId: data.fromUserId,
          answer
        });
      } catch (error) {
        console.error('Error handling offer:', error);
      }
    };

    const handleAnswer = async (data: { fromUserId: string; answer: RTCSessionDescriptionInit }) => {
      const pc = peerConnections.get(data.fromUserId);
      if (pc) {
        try {
          await pc.setRemoteDescription(data.answer);
        } catch (error) {
          console.error('Error handling answer:', error);
        }
      }
    };

    const handleIceCandidate = async (data: { fromUserId: string; candidate: RTCIceCandidateInit }) => {
      const pc = peerConnections.get(data.fromUserId);
      if (pc) {
        try {
          await pc.addIceCandidate(data.candidate);
        } catch (error) {
          console.error('Error handling ICE candidate:', error);
        }
      }
    };

    socket.on('video:participantJoined', handleParticipantJoined);
    socket.on('video:participantLeft', handleParticipantLeft);
    socket.on('video:participantsUpdate', handleParticipantsUpdate);
    socket.on('video:offer', handleOffer);
    socket.on('video:answer', handleAnswer);
    socket.on('video:iceCandidate', handleIceCandidate);

    return () => {
      socket.off('video:participantJoined', handleParticipantJoined);
      socket.off('video:participantLeft', handleParticipantLeft);
      socket.off('video:participantsUpdate', handleParticipantsUpdate);
      socket.off('video:offer', handleOffer);
      socket.off('video:answer', handleAnswer);
      socket.off('video:iceCandidate', handleIceCandidate);
    };
  }, [socket, isActive, roomId, peerConnections]);

  const createPeerConnection = (userId: string): RTCPeerConnection => {
    const pc = new RTCPeerConnection(iceServers);

    // Add local stream to peer connection
    if (localStream) {
      localStream.getTracks().forEach(track => {
        pc.addTrack(track, localStream);
      });
    }

    // Handle incoming streams
    pc.ontrack = (event) => {
      const [remoteStream] = event.streams;
      setParticipants(prev => prev.map(p =>
        p.userId === userId ? { ...p, stream: remoteStream } : p
      ));
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && socket && roomId) {
        socket.emit('video:iceCandidate', {
          roomId,
          targetUserId: userId,
          candidate: event.candidate
        });
      }
    };

    setPeerConnections(prev => new Map(prev).set(userId, pc));
    return pc;
  };

  const initializeCall = async () => {
    try {
      setConnectionStatus('connecting');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: activeTab === 'video',
        audio: true
      });

      setLocalStream(stream);
      setCallStartTime(new Date());
      setConnectionStatus('connected');

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.muted = true;

        try {
          await localVideoRef.current.play();
        } catch (playError) {
          console.warn('Auto-play prevented for local video:', playError);
        }
      }

      // Join video call via socket
      if (socket && roomId) {
        socket.emit('video:join', {
          roomId,
          mediaState: {
            video: !isCameraOff,
            audio: !isMicMuted,
            screen: false
          }
        });
      }

      onStartCall();
      console.log('Video call initialized successfully');
    } catch (error) {
      console.error('Error accessing camera/microphone:', error);
      setConnectionStatus('failed');

      let errorMessage = 'Unable to access camera/microphone.';
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Camera/microphone permission denied. Please allow access and try again.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No camera or microphone found. Please check your devices.';
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Camera/microphone is already in use by another application.';
        }
      }
      alert(errorMessage);
    }
  };

  // Screen sharing functions
  const startScreenShare = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30, max: 60 }
        },
        audio: true
      });

      setScreenShareStream(stream);

      const videoTrack = stream.getVideoTracks()[0];
      peerConnections.forEach(async (pc) => {
        const sender = pc.getSenders().find(s =>
          s.track && s.track.kind === 'video'
        );
        if (sender) {
          await sender.replaceTrack(videoTrack);
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      videoTrack.onended = () => {
        stopScreenShare();
      };

      onScreenShare();
      console.log('Screen sharing started');
    } catch (error) {
      console.error('Error starting screen share:', error);
    }
  }, [peerConnections, onScreenShare]);

  const stopScreenShare = useCallback(async () => {
    if (screenShareStream) {
      screenShareStream.getTracks().forEach(track => track.stop());
      setScreenShareStream(null);

      if (localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        peerConnections.forEach(async (pc) => {
          const sender = pc.getSenders().find(s =>
            s.track && s.track.kind === 'video'
          );
          if (sender && videoTrack) {
            await sender.replaceTrack(videoTrack);
          }
        });

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }
      }

      onScreenShare();
      console.log('Screen sharing stopped');
    }
  }, [screenShareStream, localStream, peerConnections, onScreenShare]);

  // Fullscreen functionality
  const enterFullscreen = useCallback(async () => {
    if (fullscreenContainerRef.current) {
      try {
        if (fullscreenContainerRef.current.requestFullscreen) {
          await fullscreenContainerRef.current.requestFullscreen();
        } else if ((fullscreenContainerRef.current as any).webkitRequestFullscreen) {
          await (fullscreenContainerRef.current as any).webkitRequestFullscreen();
        } else if ((fullscreenContainerRef.current as any).msRequestFullscreen) {
          await (fullscreenContainerRef.current as any).msRequestFullscreen();
        }
        setIsFullscreen(true);
      } catch (error) {
        console.error('Error entering fullscreen:', error);
      }
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const endCall = () => {
    // Stop all tracks
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    // Stop screen share stream if active
    if (screenShareStream) {
      screenShareStream.getTracks().forEach(track => track.stop());
      setScreenShareStream(null);
    }

    // Close all peer connections
    peerConnections.forEach(pc => pc.close());
    setPeerConnections(new Map());

    // Clear participants and reset state
    setParticipants([]);
    setCallStartTime(null);
    setCallDuration(0);
    setConnectionStatus('connecting');

    // Leave video call via socket
    if (socket && roomId) {
      socket.emit('video:leave', { roomId });
    }

    onEndCall();
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      peerConnections.forEach(pc => pc.close());
    };
  }, []);

  if (!isActive) {
    return (
      <div className="card h-96">
        <h3 className="text-lg font-bold mb-4">{activeTab === 'voice' ? 'Voice Call' : 'Video Conference'}</h3>
        <div className="bg-white/5 rounded-lg h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">📹</div>
            <div className="text-white/70 mb-4">Ready to start video call</div>
            <button
              onClick={initializeCall}
              className="bg-accent text-primary px-6 py-3 rounded-lg hover:bg-accent/90 font-medium"
            >
              Start Video Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while initializing (only after user clicked start)
  if (isActive && !localStream && connectionStatus === 'connecting') {
    return (
      <div className="card h-96">
        <h3 className="text-lg font-bold mb-4">Video Conference</h3>
        <div className="bg-white/5 rounded-lg h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse">📹</div>
            <div className="text-white/70 mb-4">Connecting to camera...</div>
            <div className="text-white/50 text-sm">Please allow camera access when prompted</div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if failed to initialize
  if (isActive && !localStream && connectionStatus === 'failed') {
    return (
      <div className="card h-96">
        <h3 className="text-lg font-bold mb-4">Video Conference</h3>
        <div className="bg-white/5 rounded-lg h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-white/70 mb-4">Camera access failed</div>
            <button
              onClick={initializeCall}
              className="bg-accent text-primary px-6 py-3 rounded-lg hover:bg-accent/90 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={fullscreenContainerRef} className={`card ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">{activeTab === 'voice' ? 'Voice Call' : 'Video Conference'}</h3>
        <div className="flex gap-2">
          <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
            🔴 LIVE
          </span>
          {isScreenSharing && (
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
              📺 Sharing
            </span>
          )}
          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
            {participants.length + 1} participant{participants.length !== 0 ? 's' : ''}
          </span>
          <button
            onClick={toggleFullscreen}
            className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? '⊟' : '⊞'}
          </button>
        </div>
      </div>

      {/* Video Grid */}
      <div className={`grid gap-2 mb-4 ${isFullscreen ? 'h-[calc(100vh-200px)]' : ''} ${participants.length === 0 ? 'grid-cols-1' :
        participants.length <= 1 ? 'grid-cols-2' :
          participants.length <= 3 ? 'grid-cols-2' :
            'grid-cols-3'
        }`}>
        {/* Local video */}
        <div className={`relative bg-black rounded-lg overflow-hidden ${isFullscreen ? 'h-full' : 'aspect-video'}`}>
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover transform scale-x-[-1]"
            onLoadedMetadata={() => console.log('Video metadata loaded')}
            onPlay={() => console.log('Video started playing')}
            onError={(e) => console.error('Video error:', e)}
          />

          {/* Debug info overlay */}
          {localStream && (
            <div className="absolute top-2 left-2 bg-green-500/80 text-white px-2 py-1 rounded text-xs">
              📹 Stream Active
            </div>
          )}

          {!localStream && connectionStatus === 'connected' && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl mb-2">⚠️</div>
                <div className="text-white/70">No video stream</div>
              </div>
            </div>
          )}

          {isCameraOff && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl mb-2">📷</div>
                <div className="text-white/70">Camera is off</div>
              </div>
            </div>
          )}

          <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
            You {isMicMuted && '🔇'}
          </div>
        </div>

        {/* Remote participants */}
        {participants.map(participant => (
          <RemoteVideo
            key={participant.userId}
            participant={participant}
            isFullscreen={isFullscreen}
          />
        ))}
      </div>

      {/* Controls */}
      <div className={`flex justify-center gap-3 mb-4 ${isFullscreen ? 'absolute bottom-4 left-1/2 transform -translate-x-1/2' : ''}`}>
        <button
          onClick={onToggleMic}
          className={`p-4 rounded-full ${isMicMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm transition-colors text-white`}
          title={isMicMuted ? 'Unmute' : 'Mute'}
        >
          {isMicMuted ? '🔇' : '🎤'}
        </button>

        <button
          onClick={onToggleCamera}
          className={`p-4 rounded-full ${isCameraOff ? 'bg-red-500 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm transition-colors text-white`}
          title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
        >
          {isCameraOff ? '📷' : '📹'}
        </button>

        <button
          onClick={isScreenSharing ? stopScreenShare : startScreenShare}
          className={`p-4 rounded-full ${isScreenSharing ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm transition-colors text-white`}
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          📺
        </button>

        <button
          onClick={endCall}
          className="p-4 rounded-full bg-red-500 hover:bg-red-600 backdrop-blur-sm transition-colors text-white"
          title="End call"
        >
          📞
        </button>
      </div>

      {/* Call info */}
      <div className={`flex justify-between items-center text-sm text-white/60 ${isFullscreen ? 'absolute bottom-1 left-4 right-4' : ''}`}>
        <div>Call duration: {formatDuration(callDuration)}</div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></div>
          {connectionStatus === 'connected' ?
            `Connected to ${participants.length} participant${participants.length !== 1 ? 's' : ''}` :
            'Reconnecting...'
          }
        </div>
      </div>
    </div>
  );
};

// Enhanced Remote video component
interface RemoteVideoProps {
  participant: Participant;
  isFullscreen?: boolean;
}

const RemoteVideo: React.FC<RemoteVideoProps> = ({ participant, isFullscreen = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && participant.stream) {
      videoRef.current.srcObject = participant.stream;

      // Ensure video plays
      const playVideo = async () => {
        try {
          await videoRef.current?.play();
        } catch (playError) {
          console.warn('Auto-play prevented for remote video:', playError);
        }
      };

      playVideo();
    }
  }, [participant.stream]);

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${isFullscreen ? 'h-full' : 'aspect-video'
      }`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
      />

      {!participant.mediaState.video && (
        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl mb-2">👤</div>
            <div className="text-white/70 text-sm">{participant.username}</div>
          </div>
        </div>
      )}

      {participant.mediaState.screen && (
        <div className="absolute top-2 right-2 bg-blue-500/80 text-white px-2 py-1 rounded text-xs">
          📺 Sharing
        </div>
      )}

      <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
        <span>{participant.username}</span>
        {!participant.mediaState.audio && <span>🔇</span>}
        {!participant.mediaState.video && <span>📷</span>}
      </div>

      {!participant.stream && (
        <div className="absolute top-2 left-2 bg-yellow-500/80 text-white px-2 py-1 rounded text-xs">
          Connecting...
        </div>
      )}
    </div>
  );
};

export default VideoCall;